import React from "react";
import { ProductV2, ServiceItem } from "../types";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { ServiceItemTable } from "./ServiceItemTable";
import { useTranslation } from "react-i18next";

const TRANSLATION_KEY = "settings.product";

interface ProductCardProps {
  product: ProductV2;
  index: number;
  onEdit: (product: ProductV2) => void;
  onAddServiceItem: (productId: string) => void;
  onEditServiceItem?: (serviceItem: ServiceItem) => void;
  onDeleteServiceItem?: (serviceItem: ServiceItem) => void;
  onDragEndServiceItems?: (result: any) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  index,
  onEdit,
  onAddServiceItem,
  onEditServiceItem,
  onDeleteServiceItem,
  onDragEndServiceItems
}) => {
  const { t } = useTranslation();

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    if (onDragEndServiceItems) {
      onDragEndServiceItems({
        ...result,
        productId: product.id
      });
    }
  };

  const handleEditServiceItem = (serviceItem: any) => {
    if (onEditServiceItem) {
      onEditServiceItem({ ...serviceItem, productId: product.id });
    }
  };

  return (
    <div
      className="bg-surface-elevation1 border border-border-border1 rounded-2xl"
      data-test={`products-card-${product.id}`}
    >
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-3">
          <LogoIconContainer index={index} />
          <div>
            <Text variant="body" data-test={`products-card-name-${product.id}`}>
              {product.name}
            </Text>
            <Text
              variant="smBodySecondary"
              data-test={`products-card-description-${product.id}`}
            >
              {product.description}
            </Text>
          </div>
        </div>
        <Button
          variant="tertiary"
          onClick={() => onEdit(product)}
          data-test={`products-card-edit-button-${product.id}`}
        >
          {t(`${TRANSLATION_KEY}.productCard.edit`)}
        </Button>
      </div>
      <div>
        <ServiceItemTable
          serviceItems={product.serviceItems}
          onDragEnd={handleDragEnd}
          onEditServiceItem={handleEditServiceItem}
          onDeleteServiceItem={onDeleteServiceItem}
          onAddServiceItem={() => onAddServiceItem(product.id)}
        />
      </div>
    </div>
  );
};

export default ProductCard;
